import React, { useContext, useState, useEffect, useMemo, useCallback } from "react";
import HighchartsComponent from "../../HighchartsComponent";
import { useAppContext } from "../../../contexts/AppContext";
import FiltersContext from "../../../contexts/Filters";
import { LoadingComponent } from "@gooddata/sdk-ui";

/**
 * Calculate the mode of an array
 * @param {Array} arr - The array to calculate the mode of
 * @returns {number} - The mode of the array
 */
const mode = (arr) => {
    const store = {};
    arr.forEach((num) => (store[num] ? (store[num] += 1) : (store[num] = 1)));
    return parseInt(Object.keys(store).sort((a, b) => (store[b] > store[a] ? 1 : -1))[0], 10);
};

const BasketSynthesisChart = ({ printStatus, data, listPrices, basketReference = undefined, filters, isLoading = false, width = null, title, kpi, kpiSettings, rolledForwardDataPoints = new Set(), ...props }) => {
    const id = "basket-synthesis-graph";
    const { getParamsByCustID, showListPrices: showListPricesContext } = useAppContext();

    const { getFirstFilterValue } = useContext(FiltersContext);

    const [showScatter, setShowScatter] = useState(true);
    const [showListPrice, setShowListPrice] = useState(true);

    useEffect(() => {
        setShowListPrice(showListPricesContext);
    }, [showListPricesContext]);

    const chartData = useMemo(() => {
        const chartData =
            data?.map((elem, index) => {
                const dataValues = Object.entries(elem)
                    .filter(([key, value]) => key !== "versionStd" && key !== "make" && value !== null)
                    .map(([key, value]) => value)
                    .sort((a, b) => a - b);
                return Object.assign({
                    name: elem.versionStd,
                    y: dataValues[0],
                    color: elem.versionStd === basketReference?.versionStd && "#3a0ca3",
                });
            }) || [];
        chartData?.sort((a, b) => b.y - a.y);
        return chartData;
    }, [data, basketReference]);

    const listpriceData = useMemo(() => {
        const listPriceData =
            listPrices?.map((elem, index) => {
                const dataValues = Object.entries(elem)
                    .filter(([key, value]) => key !== "versionStd" && key !== "make" && value !== null)
                    .map(([key, value]) => value)
                    .sort((a, b) => a - b);
                return Object.assign({
                    name: elem.versionStd,
                    y: mode(dataValues),
                    color: "#ff7d93",
                });
            }) || [];
        listPriceData?.sort((a, b) => {
            const aIndex = chartData.findIndex((item) => item.name === a.name);
            const bIndex = chartData.findIndex((item) => item.name === b.name);
            return aIndex - bIndex;
        });
        return listPriceData;
    }, [listPrices, chartData]);

    const getHighlightedMarkerSvg = useCallback((symbol, seriesColor, highlightColor) => {
        const width = 18,
            height = 18;
        let shapeSvg = "";

        switch (symbol) {
            case "diamond":
                shapeSvg = `<path d="M 8 2 L 14 8 L 8 14 L 2 8 Z" fill="${seriesColor}" stroke="${highlightColor}" stroke-width="2" />`;
                break;
            case "square":
                shapeSvg = `<rect x="2" y="2" width="12" height="12" fill="${seriesColor}" stroke="${highlightColor}" stroke-width="2" />`;
                break;
            case "triangle":
                shapeSvg = `<path d="M 8 2 L 15 14 L 1 14 Z" fill="${seriesColor}" stroke="${highlightColor}" stroke-width="2" />`;
                break;
            case "triangle-down":
                shapeSvg = `<path d="M 8 14 L 15 2 L 1 2 Z" fill="${seriesColor}" stroke="${highlightColor}" stroke-width="2" />`;
                break;
            case "circle":
            default:
                shapeSvg = `<circle cx="8" cy="8" r="7" fill="${seriesColor}" stroke="${highlightColor}" stroke-width="2" />`;
                break;
        }

        return `
            <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
                ${shapeSvg}
            </svg>
        `.trim();
    }, []);

    const categories = useMemo(() => chartData?.map((item) => item.name), [chartData]);
    const symbols = useMemo(() => ["circle", "diamond", "square", "triangle", "triangle-down"], []);

    const custOfferSeries = useMemo(() => {
        return (
            data?.reduce((acc, elem) => {
                const custOffersMatrix = Object.entries(elem).filter(([key, value]) => key !== "versionStd" && key !== "make" && value !== null);
                const xIndex = categories?.findIndex((category) => category === elem?.versionStd);
                custOffersMatrix?.forEach((custOffer) => {
                    const custId = custOffer[0];
                    const custObj = getParamsByCustID(custId, getFirstFilterValue("filterCountryId"));
                    const custSerie = acc.find((item) => item.id === custId);

                    const point = {
                        x: showListPrice ? xIndex - 0.14 : xIndex,
                        y: custOffer[1],
                    };

                    if (rolledForwardDataPoints.has(`${elem.versionStd}|${custId}`)) {
                        const seriesColor = (custSerie && custSerie.color) || custObj?.hexColor || "#6c757d";
                        const symbol = (custSerie && custSerie.marker.symbol) || symbols[acc.length % symbols.length];
                        const svgIcon = getHighlightedMarkerSvg(symbol, seriesColor, "#BFBFBF");
                        point.marker = {
                            symbol: `url(data:image/svg+xml;base64,${window.btoa(svgIcon)})`,
                            width: 18,
                            height: 18,
                        };
                    }

                    if (custSerie !== undefined) {
                        custSerie.data.push(point);
                    } else {
                        acc.push({
                            type: "scatter",
                            id: custId,
                            name: custObj?.custLabel || custId,
                            color: custObj?.hexColor || "#6c757d",
                            marker: {
                                symbol: symbols[acc.length % symbols.length],
                            },
                            data: [point],
                        });
                    }
                });
                return acc;
            }, []) || []
        );
    }, [data, categories, showListPrice, rolledForwardDataPoints, getHighlightedMarkerSvg, symbols]);

    return isLoading ? (
        <LoadingComponent />
    ) : (
        <div className={!printStatus && "border border-gray-200 shadow rounded"}>
            <HighchartsComponent
                enableScreenshot={!printStatus}
                widgetProps={{
                    id,
                    filters,
                    section: "basketsynthesis",
                    flag_services: kpiSettings,
                }}
                options={{
                    caption:
                        rolledForwardDataPoints && rolledForwardDataPoints.size > 0
                            ? {
                                  text: '<div style="display: flex; align-items: center; font-size: 10px; color: #666666;"><svg width="16" height="16" viewBox="0 0 16 16" style="margin-right: 4px;"><circle cx="8" cy="8" r="6" fill="#506e96" stroke="#BFBFBF" stroke-width="3"/></svg><span>Gray bordered points are from previous month</span></div>',
                                  useHTML: true,
                                  align: "left",
                                  verticalAlign: "bottom",
                                  y: 15,
                                  x: 10,
                              }
                            : {},
                    chart: {
                        height: categories.length < 6 ? 450 : 600,
                        width: width,
                    },
                    plotOptions: {
                        bar: {
                            borderRadius: 3.0,
                            enableMouseTracking: false,
                            dataLabels: {
                                enabled: true,
                                align: "right",
                                x: -20,
                                color: "#FFFFFF",
                                formatter: function () {
                                    return this.y !== 0 ? this.y : null;
                                },
                            },
                        },
                        scatter: {
                            stickyTracking: false,
                            marker: {
                                radius: 6,
                            },
                        },
                    },
                    title: {
                        text: title,
                        style: {
                            fontSize: printStatus ? "20px" : "14px",
                            padding: printStatus ? "16px" : null,
                            fontWeight: "bold",
                            color: "#4d4d4d",
                            textAlign: "center",
                        },
                    },
                    tooltip: {
                        snap: 0,
                        formatter: function () {
                            if (!["Cheapest competitor", "List Price"].includes(this.series.name)) {
                                const custId = this.series.options.id;
                                const versionStd = this.point.category;
                                const isRolledForward = rolledForwardDataPoints.has(`${versionStd}|${custId}`);
                                let tooltip = `<b>${this.series.name}</b><br/>${this.y}`;
                                if (isRolledForward) {
                                    tooltip += `<br/><b>(From previous month)</b>`;
                                }
                                return tooltip;
                            } else return `<span>${this.x}</span><br/><em>Adj.rental</em> ${this.y}`;
                        },
                    },
                    series: showListPricesContext
                        ? [
                              {
                                  name: "Cheapest competitor",
                                  type: "bar",
                                  data: chartData,
                                  color: "#506e96",
                                  yAxis: 0,
                                  pointWidth: chartData.length < 12 ? 30.0 : 25.0,
                                  //   grouping: false,
                                  events: {
                                      legendItemClick: function (event) {
                                          if (event.target.name === "Cheapest competitor") {
                                              setShowScatter(!showScatter);
                                          }
                                          return true;
                                      },
                                  },
                              },
                              {
                                  name: "List Price",
                                  type: "bar",
                                  data: listpriceData,
                                  color: "#ff7d93",
                                  yAxis: 1,
                                  pointWidth: (chartData.length < 12 ? 30.0 : 25.0) * 0.6,
                                  //   grouping: false,
                                  events: {
                                      legendItemClick: function (event) {
                                          if (event.target.name === "List Price") {
                                              setShowListPrice(!showListPrice);
                                          }
                                          return true;
                                      },
                                  },
                              },
                              ...custOfferSeries.map((serie) => ({
                                  ...serie,
                                  visible: showScatter,
                              })),
                          ]
                        : [
                              {
                                  name: "Cheapest competitor",
                                  type: "bar",
                                  data: chartData,
                                  color: "#506e96",
                                  yAxis: 0,
                                  pointWidth: 30.0,
                              },
                              ...custOfferSeries,
                          ],
                    xAxis: {
                        categories: categories,
                        labels: {
                            style: {
                                fontWeight: 600,
                                fontSize: "12px",
                                color: "#595959",
                            },
                        },
                    },
                    yAxis: showListPricesContext
                        ? [
                              {
                                  title: {
                                      text: showScatter ? title?.split("|")[4] : "List Price",
                                      style: {
                                          color: showScatter ? "#506e96" : "#ff7d93",
                                      },
                                  },
                                  labels: {
                                      style: {
                                          color: showScatter ? "#506e96" : "#ff7d93",
                                      },
                                  },
                                  alignTicks: false,
                                  plotLines: basketReference?.value && [
                                      {
                                          value: basketReference?.value,
                                          width: 2,
                                          zIndex: 10,
                                          color: "#FF0000",
                                          dashStyle: "shortdash",
                                      },
                                  ],
                              },
                              {
                                  title: {
                                      text: null,
                                  },
                                  labels: {
                                      enabled: false,
                                  },
                                  // Controls whether the y-axis appears on the opposite side of the chart (right vs left)
                                  opposite: false,
                                  // Ticks are the small marks/lines on the axis showing data points
                                  // When false, prevents this y-axis from trying to align its tick marks with other y-axes
                                  alignTicks: false,
                                  gridLineWidth: showScatter ? 0 : 1,
                                  type: "logarithmic",
                                  tickInterval: 1.5,
                              },
                          ]
                        : [
                              {
                                  title: {
                                      text: title?.split("|")[4],
                                      style: {
                                          color: "#506e96",
                                      },
                                  },
                                  labels: {
                                      style: {
                                          color: "#506e96",
                                      },
                                  },
                                  alignTicks: false,
                                  plotLines: basketReference?.value && [
                                      {
                                          value: basketReference?.value,
                                          width: 2,
                                          zIndex: 10,
                                          color: "#FF0000",
                                          dashStyle: "shortdash",
                                      },
                                  ],
                              },
                          ],
                    legend: {
                        enabled: true,
                        symbolRadius: 0,
                        symbolHeight: 16,
                        labelFormatter: function () {
                            if (this.name === "Cheapest competitor") return `<span style="width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;color:#506e96">${this.name}</span>`;
                            else return `<div style="width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${this.name}</div>`;
                        },
                    },
                }}
                {...props}
            />
        </div>
    );
};

export default BasketSynthesisChart;
